import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState, useEffect, useRef, useCallback } from 'react';
import { ApiError, authApi, isTauri, useAuthStore } from '@wordpuppi/shared';
import {
  parseAuthFragment,
  parseDesktopBounce,
  DEEP_LINK_REDIRECT,
  DEEPLINK_MSG_SOURCE,
  DEEPLINK_MSG_KIND,
} from '@/lib/socialAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SocialLoginButtons } from '@/components/SocialLoginButtons';
import { PENDING_INVITE_KEY } from './invite.$token';
import { PENDING_SHARED_TEMPLATE_KEY } from './templates.shared.$token';
import logoBlack from '@/assets/logo-black-300w.webp';
import logoWhite from '@/assets/logo-white-300w.webp';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

/** GoTrue redirects back with the session (or an error) in the URL hash. */
function parseGotrueCallback(): { accessToken?: string; error?: string } {
  const hash = window.location.hash.replace(/^#/, '');
  return parseAuthFragment(hash || window.location.search);
}

function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, isAuthenticated, setToken } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [socialExchange, setSocialExchange] = useState(false);
  // Set when a system browser lands here mid desktop-OAuth (AB#333): the URL we
  // then deep-link back into the app. Non-null flips the Card to the "return to
  // the app" fallback.
  const [desktopBounce, setDesktopBounce] = useState<string | null>(null);
  // Set when the desktop OAuth round-trip came back with a provider error: the
  // full-screen interstitial shows its error variant instead of the web form.
  const [desktopBounceError, setDesktopBounceError] = useState<string | null>(null);
  const hasRedirected = useRef(false);
  const handledCallback = useRef(false);
  // A system browser opened by the desktop app carries `?wpp_desktop=1` (AB#504,
  // the same marker the social flow uses). When present, a successful login here
  // is a HANDOFF: end at a terminal "you can close this window" screen instead of
  // mounting the full web admin. Captured once at mount, before any
  // history.replaceState. isTauri() excludes the wrapped in-app iframe; plain web
  // never sets the marker, so plain web is completely unaffected.
  const [isDesktopHandoff] = useState(
    () => !isTauri() && new URLSearchParams(window.location.search).get('wpp_desktop') === '1',
  );

  // Exchange a GoTrue access token for an app session. Shared by the web hash
  // callback and the desktop deep-link message path so the backend call lives
  // in exactly one place (AB#333).
  const runSocialExchange = useCallback(
    (accessToken: string) => {
      setSocialExchange(true);
      authApi
        .socialLogin(accessToken)
        .then((res) => {
          if (res.data?.token) {
            setToken(res.data.token);
          } else {
            setError('Social sign-in failed. Please try again.');
          }
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Social sign-in failed');
        })
        .finally(() => setSocialExchange(false));
    },
    [setToken],
  );

  // Web social round-trip: GoTrue redirects back to /login with the session in
  // the URL hash. Exchange it, then fall into the normal authenticated redirect.
  useEffect(() => {
    if (handledCallback.current) return;
    handledCallback.current = true;

    // Desktop bounce (AB#333): a system browser landed here after the desktop
    // OAuth round-trip (the `wpp_desktop=1` marker set by SocialLoginButtons).
    // Hand the fragment back to the app via the `wordpuppi://auth/social` deep
    // link (shell.ts's consumer) INSTEAD of exchanging it as a web session —
    // that would log this browser in, not the desktop app. Plain browsers only;
    // the desktop iframe (isTauri) never bounces.
    if (!isTauri()) {
      const bounce = parseDesktopBounce(window.location.search, window.location.hash);
      if (bounce.isDesktop && bounce.fragment) {
        const { error: bounceError } = parseAuthFragment(bounce.fragment);
        if (bounceError) {
          // Provider denied — show the desktop error interstitial (not the web
          // form; this browser never asked to log in). No session to hand off.
          setDesktopBounceError(bounceError);
          window.history.replaceState(null, '', window.location.pathname);
          return;
        }
        const target = `${DEEP_LINK_REDIRECT}#${bounce.fragment}`;
        setDesktopBounce(target);
        // Auto-attempt the custom-scheme nav; browsers often gate it behind a
        // user gesture, so the fallback button below guarantees recovery.
        window.location.href = target;
        return;
      }
    }

    const { accessToken, error: callbackError } = parseGotrueCallback();
    if (callbackError) {
      // e.g. the user denied consent at the provider — friendly, not a dead end.
      setError(callbackError);
      window.history.replaceState(null, '', window.location.pathname);
      return;
    }
    if (!accessToken) return;

    window.history.replaceState(null, '', window.location.pathname);
    runSocialExchange(accessToken);
  }, [runSocialExchange]);

  // Desktop social round-trip (AB#333): the Tauri shell catches the
  // `wordpuppi://auth/social` deep link and postMessages the GoTrue fragment
  // into this same-origin iframe. Same exchange, no browser strand.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { source?: string; kind?: string; fragment?: unknown };
      if (
        data?.source !== DEEPLINK_MSG_SOURCE ||
        data?.kind !== DEEPLINK_MSG_KIND ||
        typeof data.fragment !== 'string'
      ) {
        return;
      }
      const { accessToken, error: callbackError } = parseAuthFragment(data.fragment);
      if (callbackError) {
        setError(callbackError);
        return;
      }
      if (accessToken) runSocialExchange(accessToken);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [runSocialExchange]);

  // After auth, return to a pending team invite (AB#451) if one is stashed,
  // otherwise go home. Guarded so the effect + handleSubmit can't double-fire.
  const finishAuth = () => {
    if (hasRedirected.current) return;
    hasRedirected.current = true;
    // #622: passive desktop bridge — a fresh login is a natural "look again"
    // moment for the silent update check (closes the update-published-after-
    // launch-check hole). The shell listens for this key; inert on web.
    localStorage.setItem('wpp-login-ping', String(Date.now()));
    const pending = localStorage.getItem(PENDING_INVITE_KEY);
    if (pending) {
      localStorage.removeItem(PENDING_INVITE_KEY);
      navigate({ to: '/invite/$token', params: { token: pending } });
    } else if (sessionStorage.getItem(PENDING_SHARED_TEMPLATE_KEY)) {
      const token = sessionStorage.getItem(PENDING_SHARED_TEMPLATE_KEY)!;
      sessionStorage.removeItem(PENDING_SHARED_TEMPLATE_KEY);
      navigate({ to: '/templates/shared/$token', params: { token } });
    } else {
      navigate({ to: '/' });
    }
  };

  // Redirect if already authenticated — UNLESS this is a desktop handoff, where
  // the browser must stop at the terminal screen rather than enter the web app.
  useEffect(() => {
    if (isAuthenticated && !isDesktopHandoff) {
      finishAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Desktop handoff (AB#504): the desktop app opened this system browser for
  // sign-in. Once authenticated by ANY method (email/password or social), end
  // here — the installed app owns the session; don't mount the web admin.
  if (isDesktopHandoff && isAuthenticated) {
    return (
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="w-full max-w-[420px] space-y-6 text-center">
          <div className="flex justify-center">
            <img
              src={logoBlack}
              alt="WordPuppi"
              className="dark:hidden"
              style={{ height: 'calc(var(--spacing) * 14)' }}
            />
            <img
              src={logoWhite}
              alt="WordPuppi"
              className="hidden dark:block"
              style={{ height: 'calc(var(--spacing) * 14)' }}
            />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold">You&apos;re signed in</h1>
            <p className="text-sm text-muted-foreground">
              You can close this window and return to the WordPuppi app.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  // Desktop OAuth landing (AB#333): a system browser finished the round-trip and
  // is here only to hand the session back to the installed app (or report that
  // it failed). Render a dedicated full-screen interstitial — no web login form,
  // no social buttons — matching the Claude/Figma browser-to-desktop pattern.
  if (desktopBounce || desktopBounceError) {
    return (
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="w-full max-w-[420px] space-y-6 text-center">
          <div className="flex justify-center">
            <img
              src={logoBlack}
              alt="WordPuppi"
              className="dark:hidden"
              style={{ height: 'calc(var(--spacing) * 14)' }}
            />
            <img
              src={logoWhite}
              alt="WordPuppi"
              className="hidden dark:block"
              style={{ height: 'calc(var(--spacing) * 14)' }}
            />
          </div>
          {desktopBounceError ? (
            <div className="space-y-3">
              <h1 className="text-2xl font-semibold">Sign-in didn&apos;t finish</h1>
              <p className="text-sm text-muted-foreground">{desktopBounceError}</p>
              <p className="text-sm text-muted-foreground">
                Return to the WordPuppi app and try again.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <h1 className="text-2xl font-semibold">You&apos;re signed in</h1>
                <p className="text-sm text-muted-foreground">
                  Opening the WordPuppi desktop app…
                </p>
              </div>
              <Button asChild className="w-full">
                <a href={desktopBounce ?? undefined}>Open WordPuppi</a>
              </Button>
              <p className="text-sm text-muted-foreground">
                If your browser asks, allow it to open WordPuppi. You can close this tab once
                the app opens.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await login(email, password);
      // Desktop handoff renders the terminal "close this window" screen instead
      // of entering the web admin (AB#504); plain web continues as before.
      if (!isDesktopHandoff) finishAuth();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Don't surface the raw backend "unauthorized!" string (AB#373)
        setError('Incorrect email or password.');
      } else if (err instanceof ApiError && err.status >= 500) {
        setError('Something went wrong on our end. Please try again.');
      } else {
        setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
      }
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-[400px]">
        <CardHeader>
          <div className="flex justify-center">
            <img
              src={logoBlack}
              alt="WordPuppi"
              className="dark:hidden"
              style={{ height: 'calc(var(--spacing) * 14)' }}
            />
            <img
              src={logoWhite}
              alt="WordPuppi"
              className="hidden dark:block"
              style={{ height: 'calc(var(--spacing) * 14)' }}
            />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot"
                  className="text-sm text-primary underline-offset-4 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            <Button type="submit" disabled={isLoading || socialExchange} className="w-full">
              {socialExchange ? 'Signing you in...' : isLoading ? 'Signing in...' : 'Sign In'}
            </Button>

            <SocialLoginButtons />

            <p className="text-center text-sm text-muted-foreground">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-primary underline-offset-4 hover:underline">
                Create account
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
